import styled from 'styled-components';
import {
  apiErrorText,
  useGetPtoVolumeHistoryQuery,
  type PtoRdEditsAccess,
  type PtoVolumeEdit,
} from '@shared/api';
import {
  dateTimeRu,
  kindLabel,
  logEntryView,
  reasonLabel,
  sheetRefText,
  statusLabel,
  valuesText,
} from './logic';
import { ErrorText, Muted } from './ui';
import { LogEntry } from './LogEntry';

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const H = styled.h4`
  margin: ${({ theme }) => theme.spacing(2)} 0 0;
  font-size: 14px;
`;

const Edit = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  font-size: 13px;
  line-height: 1.5;
`;

const Strong = styled.span`
  font-weight: 600;
`;

function EditItem({ e, access }: { e: PtoVolumeEdit; access: PtoRdEditsAccess | null }) {
  const sheet = sheetRefText(e.sheet_ref);
  return (
    <Edit>
      <div>
        <Strong>{kindLabel(e.kind)}</Strong> · {statusLabel(e.status)}
      </div>
      <Muted>
        {e.created_by_name ?? 'пользователь'}, {dateTimeRu(e.created_at)}
        {e.confirmed_by_name
          ? ` · подтвердил ${e.confirmed_by_name}, ${dateTimeRu(e.confirmed_at)}`
          : ''}
      </Muted>
      {e.machine && <div>Прочтение программы: {valuesText(e.machine)}</div>}
      <div>
        {e.kind === 'per_unit' && e.per_unit_multiplier
          ? `Кратность × ${e.per_unit_multiplier}: `
          : 'Значение инженера: '}
        {valuesText(e.value)}
      </div>
      {sheet && <div>Лист: {sheet}</div>}
      {e.reason_code && <div>Причина: {reasonLabel(e.reason_code, access)}</div>}
      {e.urgency && <div>Обоснование крайней необходимости: {e.urgency}</div>}
      {e.comment && <div>Комментарий: {e.comment}</div>}
      {e.closed_reason && <div>Закрыто: {e.closed_reason}</div>}
    </Edit>
  );
}

/** История позиции: правки (прочтение программы, значение инженера, лист,
 *  причина) и записи неудаляемого журнала (кто, роль, когда, «было → стало»). */
export function HistoryView({
  positionId,
  access,
}: {
  positionId: string;
  access: PtoRdEditsAccess | null;
}) {
  const { data, isLoading, error } = useGetPtoVolumeHistoryQuery(positionId);
  if (isLoading) return <Muted>Загрузка истории…</Muted>;
  if (error)
    return <ErrorText>{apiErrorText(error, 'Не удалось загрузить историю позиции.')}</ErrorText>;
  if (!data) return null;
  return (
    <Block>
      <H>Правки</H>
      {data.edits.length === 0 ? (
        <Muted>Правок нет.</Muted>
      ) : (
        data.edits.map((e) => <EditItem key={e.id} e={e} access={access} />)
      )}
      <H>Журнал</H>
      {data.log.length === 0 ? (
        <Muted>Записей журнала нет.</Muted>
      ) : (
        [...data.log].reverse().map((e) => <LogEntry key={e.id} view={logEntryView(e, access)} />)
      )}
      <Muted>Журнал только дополняется: записи не изменяются и не удаляются.</Muted>
    </Block>
  );
}
